import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import * as api from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // true until we've checked any stored session

  const refreshUser = useCallback(async () => {
    if (!api.tokens.access && !api.tokens.refresh) {
      setUser(null);
      return null;
    }
    try {
      const me = await api.getMe();
      setUser(me);
      return me;
    } catch {
      api.logout();
      setUser(null);
      return null;
    }
  }, []);

  // Hydrate from any persisted session on first mount.
  useEffect(() => {
    (async () => {
      await refreshUser();
      setLoading(false);
    })();
  }, [refreshUser]);

  const login = useCallback(async (username, password) => {
    await api.login(username, password);
    return refreshUser();
  }, [refreshUser]);

  const register = useCallback(async (payload) => {
    await api.register(payload);
    // Auto-login with the credentials just used to register.
    await api.login(payload.username, payload.password);
    return refreshUser();
  }, [refreshUser]);

  const logout = useCallback(() => {
    api.logout();
    setUser(null);
  }, []);

  const value = { user, loading, login, register, logout, refreshUser };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
